/**
 * Global "what is the user currently looking at" context.
 *
 * StudyViewer publishes the active study + patient summary here on mount
 * so that chrome elements outside its subtree — mainly the PatientBanner
 * in the app header — can mirror the state without prop-drilling through
 * the router. StudyViewer also clears it on unmount so other pages
 * (Studies list, Worklist, Reports) correctly show "no active study".
 */
import { createSignal } from "solid-js";

export interface ActiveStudy {
  studyUid:   string;
  patientId?: string;          // MRN
  patientName?: string;
  patientSex?: string;
  patientDob?: string;         // YYYY-MM-DD
  modality?:  string;
  studyDate?: string;
  description?: string;
  instanceCount?: number;
  activeInstance?: number;     // 1-based
}

const [activeStudy, setActiveStudy] = createSignal<ActiveStudy | null>(null);

export { activeStudy, setActiveStudy };

/** Merge a patch into the current active study — keeps the identity
 *  fields stable while allowing slice counters to update live.
 *
 *  Uses Solid's functional-updater form: the previous value is read
 *  WITHOUT adding a reactive dependency. A plain `activeStudy()`
 *  read here was the "Maximum call stack size exceeded" offender —
 *  when called inside a createEffect, each set produced a new object
 *  which re-fired the effect, which produced another new object,
 *  forever. The functional form breaks the cycle. */
export function updateActiveStudy(patch: Partial<ActiveStudy>) {
  setActiveStudy((cur) => (cur ? { ...cur, ...patch } : cur));
}
